import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * <q-product-feed> — the endless home feed (sections/custom-product-feed.liquid).
 *
 * - The next page is shown when the sentinel (right after the last real
 *   product, before the loading cards) comes within ~1.5 screens of view.
 *   While pages remain, the list ends in two rows of skeleton cards and the
 *   footer is hidden (custom-sections.css); both go when the last page is in.
 * - Always one page ahead: as soon as a page is shown, the next one is
 *   fetched in the background (not on Save-Data), so reaching it is usually
 *   instant instead of a wait at the loading cards.
 * - Desktop scrolls Horizon's `.page-wrapper`, not the window. An observer on
 *   the window cannot see "1.5 screens ahead" inside it (the wrapper clips the
 *   sentinel), so the scrolling element is the observer's root (2026-10-16:
 *   desktop only loaded once the sentinel itself was on screen).
 * - After every page the observer is RE-ARMED (unobserve + observe), which
 *   makes the browser report the sentinel's current position on its next
 *   frame. An observer only reports CHANGES: on a fast fling the sentinel was
 *   "in range" before a page was appended and "in range" again by the next
 *   frame, so no new report ever came and the feed stopped on its loading
 *   cards with the next page already downloaded (2026-10-26, reported on a
 *   phone; reproduced: 24 products, page 3 fetched, never shown).
 * - One request per page; a product already in the feed is never added twice.
 * - The address bar is never changed (Back leaves the home page).
 * - A failed request is retried 3 s later (no retry storm while offline), or
 *   as soon as the sentinel comes into view again.
 * - Out of products: the sentinel goes, the "seen it all" line shows.
 *
 * @extends {Component<{ grid: HTMLElement, loading?: HTMLElement, sentinel?: HTMLElement, end: HTMLElement }>}
 */
class ProductFeed extends Component {
  requiredRefs = ['grid', 'end'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  #showing = false;

  /** @type {{ url: string, page: Promise<{ items: Element[], nextUrl: string | null }> } | null} */
  #prefetched = null;

  #wide = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.sentinel || !this.dataset.nextUrl) return;
    this.#observe();
    this.#wide.addEventListener('change', this.#observe);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#wide.removeEventListener('change', this.#observe);
  }

  /** (Re)creates the observer on whatever scrolls the page at this width */
  #observe = () => {
    const { sentinel } = this.refs;
    this.#observer?.disconnect();
    if (!sentinel || !this.dataset.nextUrl) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#showNext();
      },
      { root: scrollRoot(this), rootMargin: '0px 0px 150% 0px' }
    );
    this.#observer.observe(sentinel);
  };

  /**
   * Fetches a page and keeps its products (once per URL)
   * @param {string} next
   */
  #fetch(next) {
    if (this.#prefetched?.url === next) return this.#prefetched.page;

    const page = (async () => {
      const url = new URL(next, window.location.origin);
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', true, url);
      const feed = new DOMParser().parseFromString(html, 'text/html').querySelector('q-product-feed');
      return {
        items: [...(feed?.querySelectorAll('.q-feed__item') ?? [])],
        nextUrl: feed?.getAttribute('data-next-url') ?? null,
      };
    })();

    this.#prefetched = { url: next, page };
    // A failed fetch is forgotten, so the next attempt asks again
    page.catch(() => {
      if (this.#prefetched?.page === page) this.#prefetched = null;
    });
    return page;
  }

  /** Background fetch of the next page, so it is ready when the shopper gets there */
  #prefetchNext() {
    const next = this.dataset.nextUrl;
    // @ts-ignore — Network Information API (Chromium)
    if (!next || navigator.connection?.saveData) return;
    this.#fetch(next).catch(() => {});
  }

  async #showNext() {
    const next = this.dataset.nextUrl;
    if (this.#showing || !next) return;
    this.#showing = true;
    let failed = false;

    try {
      const { items, nextUrl } = await this.#fetch(next);
      const seen = new Set([...this.refs.grid.children].map((item) => /** @type {HTMLElement} */ (item).dataset.productId));
      this.refs.grid.append(...items.filter((item) => !seen.has(/** @type {HTMLElement} */ (item).dataset.productId)));

      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
        this.#prefetchNext();
      } else {
        this.#finish();
      }
    } catch (error) {
      failed = true;
      console.warn('[product-feed]', error);
    } finally {
      this.#showing = false;
      if (failed) {
        setTimeout(this.#rearm, 3000);
      } else {
        this.#rearm();
      }
    }
  }

  /**
   * Asks the observer for a fresh report of where the sentinel is now (an
   * observed element always gets one report right after observe()). Still in
   * range — short pages, tall screens, a fling that is still going — the next
   * page follows at once; out of range, the next scroll into range triggers it.
   */
  #rearm = () => {
    const { sentinel } = this.refs;
    if (!this.#observer || !sentinel?.isConnected || !this.dataset.nextUrl) return;
    this.#observer.unobserve(sentinel);
    this.#observer.observe(sentinel);
  };

  #finish() {
    delete this.dataset.nextUrl;
    this.#observer?.disconnect();
    this.refs.sentinel?.remove();
    this.refs.loading?.remove();
    this.refs.end.hidden = false;
  }
}

/**
 * The element that scrolls the page: Horizon's `.page-wrapper` where it is
 * a scroll container (desktop), else the viewport (null).
 * @param {Element} from
 * @returns {Element | null}
 */
function scrollRoot(from) {
  const wrapper = from.closest('.page-wrapper');
  if (!wrapper) return null;
  const overflow = getComputedStyle(wrapper).overflowY;
  return overflow === 'auto' || overflow === 'scroll' ? wrapper : null;
}

if (!customElements.get('q-product-feed')) {
  customElements.define('q-product-feed', ProductFeed);
}
